/**
 * FahrzeugQRScanPage — QR-Scan am Schlüsselkasten (Issue #204)
 *
 * Mitarbeiter öffnet diese Seite mobil, scannt den QR-Code am Schlüsselkasten
 * (Format `gutachter-koop://fahrzeug/<uuid>`) und wird sofort der offenen
 * Poolfahrzeug-Reservierung als Fahrer zugewiesen. Ist noch keine Reservierung
 * offen, wird eine neue angelegt.
 *
 * Die Seite ist mobile-first und versucht zuerst die native BarcodeDetector-API;
 * wenn die nicht verfügbar ist, bleibt die Kamera-Vorschau aktiv und der
 * Mitarbeiter kann die URL manuell einfügen (Fallback).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'wouter';
import { Camera, CheckCircle2, XCircle, ArrowLeft, Loader2, Keyboard } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { parseFahrzeugQR, FAHRZEUG_QR_SCHEME } from '@/lib/qr-fahrzeug';

const KOOP_RED = '#C41E3A';

interface Fahrzeug {
  id: string;
  marke: string;
  modell: string;
  kennzeichen: string | null;
}

interface BarcodeDetectorLike {
  detect(source: HTMLVideoElement): Promise<Array<{ rawValue: string }>>;
}

type ScanState =
  | { status: 'idle' }
  | { status: 'requesting_camera' }
  | { status: 'scanning' }
  | { status: 'camera_denied'; message: string }
  | { status: 'processing' }
  | { status: 'success'; fahrzeug: Fahrzeug }
  | { status: 'error'; message: string };

async function zuordnenFahrer(
  fahrzeugId: string,
  mitarbeiterId: string,
): Promise<{ fahrzeug: Fahrzeug; neu: boolean }> {
  const { data: fahrzeug, error: fahrzeugErr } = await supabase
    .from('fahrzeuge')
    .select('id, marke, modell, kennzeichen')
    .eq('id', fahrzeugId)
    .maybeSingle();
  if (fahrzeugErr) throw new Error(fahrzeugErr.message);
  if (!fahrzeug) throw new Error('Fahrzeug nicht gefunden');

  const { data: offen, error: offenErr } = await supabase
    .from('pool_reservierungen')
    .select('id')
    .eq('fahrzeug_id', fahrzeugId)
    .is('ende_zeit', null)
    .order('beginn_zeit', { ascending: false })
    .limit(1);
  if (offenErr) throw new Error(offenErr.message);

  if (offen && offen.length > 0) {
    const { error: updErr } = await supabase
      .from('pool_reservierungen')
      .update({
        fahrer_mitarbeiter_id: mitarbeiterId,
        zuordnung_quelle: 'qr_scan',
        updated_at: new Date().toISOString(),
      })
      .eq('id', offen[0].id);
    if (updErr) throw new Error(updErr.message);
    return { fahrzeug: fahrzeug as Fahrzeug, neu: false };
  }

  const { error: insErr } = await supabase.from('pool_reservierungen').insert({
    fahrzeug_id: fahrzeugId,
    fahrer_mitarbeiter_id: mitarbeiterId,
    beginn_zeit: new Date().toISOString(),
    zuordnung_quelle: 'qr_scan',
  });
  if (insErr) throw new Error(insErr.message);
  return { fahrzeug: fahrzeug as Fahrzeug, neu: true };
}

export default function FahrzeugQRScanPage() {
  const { profile, loading: authLoading } = useAuth();
  const [, setLocation] = useLocation();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const detectIntervalRef = useRef<number | null>(null);
  const [state, setState] = useState<ScanState>({ status: 'idle' });
  const [manualInput, setManualInput] = useState('');
  const [manualOpen, setManualOpen] = useState(false);

  const stopStream = useCallback(() => {
    if (detectIntervalRef.current) {
      window.clearInterval(detectIntervalRef.current);
      detectIntervalRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const handleScannedUrl = useCallback(
    async (raw: string) => {
      const uuid = parseFahrzeugQR(raw);
      if (!uuid) {
        setState({
          status: 'error',
          message: `Kein gültiger Fahrzeug-QR-Code (erwartet ${FAHRZEUG_QR_SCHEME}://fahrzeug/<uuid>)`,
        });
        return;
      }
      if (!profile?.id) {
        setState({ status: 'error', message: 'Du bist nicht eingeloggt.' });
        return;
      }
      stopStream();
      setState({ status: 'processing' });
      try {
        const { fahrzeug, neu } = await zuordnenFahrer(uuid, profile.id);
        setState({ status: 'success', fahrzeug });
        toast.success(
          neu
            ? `Neue Fahrt gestartet: ${fahrzeug.marke} ${fahrzeug.modell}`
            : `Fahrt übernommen: ${fahrzeug.marke} ${fahrzeug.modell}`,
        );
      } catch (e) {
        const message = e instanceof Error ? e.message : 'Unbekannter Fehler';
        setState({ status: 'error', message });
      }
    },
    [profile?.id, stopStream],
  );

  const startCamera = useCallback(async () => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setState({
        status: 'camera_denied',
        message: 'Kamera wird von diesem Browser nicht unterstützt.',
      });
      return;
    }
    setState({ status: 'requesting_camera' });
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => undefined);
      }
      setState({ status: 'scanning' });

      const BarcodeDetectorCtor = (window as unknown as {
        BarcodeDetector?: new (opts: { formats: string[] }) => BarcodeDetectorLike;
      }).BarcodeDetector;
      if (!BarcodeDetectorCtor) {
        toast.message('Kein nativer QR-Scanner — bitte URL manuell einfügen.');
        setManualOpen(true);
        return;
      }
      const detector = new BarcodeDetectorCtor({ formats: ['qr_code'] });
      detectIntervalRef.current = window.setInterval(async () => {
        if (!videoRef.current || videoRef.current.readyState < 2) return;
        try {
          const results = await detector.detect(videoRef.current);
          if (results.length > 0 && results[0].rawValue) {
            await handleScannedUrl(results[0].rawValue);
          }
        } catch {
          /* ignore per-frame detect errors */
        }
      }, 400);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Kamera-Zugriff verweigert';
      setState({ status: 'camera_denied', message });
    }
  }, [handleScannedUrl]);

  useEffect(() => {
    return () => stopStream();
  }, [stopStream]);

  const resetAndScanAgain = () => {
    setState({ status: 'idle' });
    setManualInput('');
    setManualOpen(false);
  };

  const handleManualSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await handleScannedUrl(manualInput);
  };

  const status = state.status;

  return (
    <div className="min-h-[100dvh] bg-black text-white flex flex-col">
      <header className="flex items-center justify-between p-4 bg-gray-900">
        <button
          type="button"
          onClick={() => setLocation('/fahrzeuge')}
          className="flex items-center gap-2 text-sm text-gray-300 hover:text-white"
          aria-label="Zurück zu Fahrzeuge"
        >
          <ArrowLeft className="w-4 h-4" /> Fahrzeuge
        </button>
        <h1 className="text-base font-semibold">Fahrzeug-QR scannen</h1>
        <button
          type="button"
          onClick={() => setManualOpen((v) => !v)}
          className="flex items-center gap-1 text-sm text-gray-300 hover:text-white"
          aria-label="URL manuell eingeben"
        >
          <Keyboard className="w-4 h-4" /> Manuell
        </button>
      </header>

      <main className="flex-1 flex flex-col items-center justify-center gap-6 p-4">
        {authLoading && (
          <div className="flex items-center gap-2 text-gray-300">
            <Loader2 className="w-5 h-5 animate-spin" /> Lade Profil…
          </div>
        )}

        {!authLoading && status === 'idle' && (
          <div className="w-full max-w-sm flex flex-col gap-4 items-center text-center">
            <Camera className="w-16 h-16 text-gray-400" />
            <p className="text-gray-200">
              Kamera aktivieren und den QR-Code am Schlüsselkasten scannen.
            </p>
            <button
              type="button"
              onClick={startCamera}
              className="w-full rounded-xl px-4 py-3 font-semibold text-white"
              style={{ backgroundColor: KOOP_RED }}
            >
              Kamera starten
            </button>
          </div>
        )}

        {(status === 'requesting_camera' || status === 'scanning') && (
          <div className="w-full max-w-md flex flex-col items-center gap-4">
            <div className="relative w-full aspect-square rounded-2xl overflow-hidden bg-gray-800">
              <video
                ref={videoRef}
                className="absolute inset-0 w-full h-full object-cover"
                playsInline
                muted
              />
              <div className="absolute inset-8 border-4 border-white/70 rounded-2xl pointer-events-none" />
            </div>
            <p className="text-sm text-gray-300">
              {status === 'requesting_camera'
                ? 'Frage Kamera-Berechtigung an…'
                : 'QR-Code ins Rechteck halten.'}
            </p>
          </div>
        )}

        {status === 'camera_denied' && (
          <div className="w-full max-w-sm flex flex-col gap-4 items-center text-center">
            <XCircle className="w-14 h-14 text-red-400" />
            <p className="text-gray-200">{state.message}</p>
            <button
              type="button"
              onClick={() => setManualOpen(true)}
              className="w-full rounded-xl px-4 py-3 font-semibold bg-gray-800 text-white"
            >
              URL manuell eingeben
            </button>
          </div>
        )}

        {status === 'processing' && (
          <div className="flex items-center gap-2 text-gray-200">
            <Loader2 className="w-5 h-5 animate-spin" /> Ordne Fahrer zu…
          </div>
        )}

        {status === 'success' && (
          <div className="w-full max-w-sm flex flex-col gap-4 items-center text-center">
            <CheckCircle2 className="w-16 h-16 text-green-400" />
            <div>
              <p className="text-lg font-semibold">Fahrt übernommen</p>
              <p className="text-gray-300">
                {state.fahrzeug.marke} {state.fahrzeug.modell}
                {state.fahrzeug.kennzeichen ? ` · ${state.fahrzeug.kennzeichen}` : ''}
              </p>
              <p className="text-gray-400 mt-1 text-sm">
                Zugeordnet an {profile?.vorname} {profile?.nachname}
              </p>
            </div>
            <div className="flex flex-col gap-2 w-full">
              <button
                type="button"
                onClick={resetAndScanAgain}
                className="w-full rounded-xl px-4 py-3 font-semibold text-white"
                style={{ backgroundColor: KOOP_RED }}
              >
                Nächsten Scan
              </button>
              <button
                type="button"
                onClick={() => setLocation('/fahrzeuge')}
                className="w-full rounded-xl px-4 py-3 font-semibold bg-gray-800 text-white"
              >
                Fertig
              </button>
            </div>
          </div>
        )}

        {status === 'error' && (
          <div className="w-full max-w-sm flex flex-col gap-4 items-center text-center">
            <XCircle className="w-14 h-14 text-red-400" />
            <p className="text-gray-200">{state.message}</p>
            <button
              type="button"
              onClick={resetAndScanAgain}
              className="w-full rounded-xl px-4 py-3 font-semibold text-white"
              style={{ backgroundColor: KOOP_RED }}
            >
              Nochmal versuchen
            </button>
          </div>
        )}

        {manualOpen && status !== 'success' && status !== 'processing' && (
          <form
            onSubmit={handleManualSubmit}
            className="w-full max-w-sm flex flex-col gap-2 bg-gray-900 p-4 rounded-xl"
            aria-label="Manuelle URL-Eingabe"
          >
            <label className="text-sm text-gray-300" htmlFor="manual-qr-url">
              URL manuell einfügen
            </label>
            <input
              id="manual-qr-url"
              type="text"
              value={manualInput}
              onChange={(e) => setManualInput(e.target.value)}
              placeholder={`${FAHRZEUG_QR_SCHEME}://fahrzeug/<uuid>`}
              className="w-full rounded-lg px-3 py-2 bg-gray-800 text-white placeholder-gray-500 border border-gray-700 focus:border-white outline-none"
              autoComplete="off"
              spellCheck={false}
              data-testid="manual-qr-input"
            />
            <button
              type="submit"
              className="w-full rounded-lg px-3 py-2 font-semibold text-white"
              style={{ backgroundColor: KOOP_RED }}
            >
              Übernehmen
            </button>
          </form>
        )}
      </main>
    </div>
  );
}
